// src/pages/AffectationsControleursCreate.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

const COLORS = {
  primary: "#0B3C5D",
  secondary: "#1E88C8",
  accent: "#4FC3F7",
  darkText: "#2E2E2E",
  lightBg: "#F5F7FA",
  white: "#FFFFFF",
  danger: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  info: "#2196f3",
  successLight: "#e8f5e9",
  dangerLight: "#ffebee",
  warningLight: "#fff3e0",
  infoLight: "#e3f2fd",
};

// Sélecteur unique avec recherche (contrôleur)
const SelectWithSearch = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || placeholder;

  return (
    <div className="position-relative" ref={containerRef}>
      <div
        className="form-select"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          backgroundColor: "#fff",
          borderColor: "#e0e0e0",
          borderRadius: "8px",
          padding: "10px 12px",
          position: "relative",
        }}
      >
        {selectedLabel}
        <i
          className={`bi bi-chevron-${isOpen ? "up" : "down"} position-absolute`}
          style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}
        ></i>
      </div>

      {isOpen && (
        <div
          className="position-absolute bg-white shadow"
          style={{
            zIndex: 1000,
            width: "100%",
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            marginTop: "4px",
          }}
        >
          <div className="p-2 border-bottom">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{ borderRadius: "6px" }}
            />
          </div>

          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.value}
                className="p-2"
                onClick={() => handleSelect(opt.value)}
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    opt.value === value ? "#E3F2FD" : "transparent",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#F5F7FA")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor =
                    opt.value === value ? "#E3F2FD" : "transparent")
                }
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="p-2 text-muted">Aucun résultat</div>
          )}
        </div>
      )}
    </div>
  );
};

// Sélecteur multiple avec recherche (sites)
const MultiSelectWithSearch = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  );

  const isSelected = (optValue) => value.includes(optValue);

  const toggleOption = (optValue) => {
    const newValue = isSelected(optValue)
      ? value.filter((v) => v !== optValue)
      : [...value, optValue];
    onChange(newValue);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayText =
    value.length > 0 ? `${value.length} site(s) sélectionné(s)` : placeholder;

  return (
    <div className="position-relative" ref={containerRef}>
      <div
        className="form-select"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          backgroundColor: "#fff",
          borderColor: "#e0e0e0",
          borderRadius: "8px",
          padding: "10px 12px",
          position: "relative",
        }}
      >
        {displayText}
        <i
          className={`bi bi-chevron-${isOpen ? "up" : "down"} position-absolute`}
          style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}
        ></i>
      </div>

      {isOpen && (
        <div
          className="position-absolute bg-white shadow"
          style={{
            zIndex: 1000,
            width: "100%",
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            marginTop: "4px",
          }}
        >
          <div className="p-2 border-bottom">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Rechercher un site..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{ borderRadius: "6px" }}
            />
          </div>

          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.value}
                className="p-2 d-flex align-items-center"
                onClick={() => toggleOption(opt.value)}
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected(opt.value)
                    ? "#E3F2FD"
                    : "transparent",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#F5F7FA")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = isSelected(opt.value)
                    ? "#E3F2FD"
                    : "transparent")
                }
              >
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  checked={isSelected(opt.value)}
                  onChange={() => {}}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor: "pointer" }}
                />
                <span>{opt.label}</span>
              </div>
            ))
          ) : (
            <div className="p-2 text-muted">Aucun résultat</div>
          )}
        </div>
      )}
    </div>
  );
};

const AffectationsControleursCreate = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [controleurs, setControleurs] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedControleur, setSelectedControleur] = useState("");
  const [selectedSites, setSelectedSites] = useState([]);
  const [results, setResults] = useState(null);

  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserInfo({
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0,
        });
      } catch {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!userInfo) return;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      try {
        // Contrôleurs
        let controleursData = [];
        try {
          const response = await axios.get(
            `${API_BASE_URL}/users/list.php?profile=2&limit=1000`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (response.data?.success) {
            controleursData = response.data.data || response.data.users || [];
          }
        } catch (err) {
          console.error("Erreur contrôleurs :", err);
        }
        setControleurs(controleursData);

        // Sites non affectés
        let sitesData = [];
        try {
          const response = await axios.get(
            `${API_BASE_URL}/sites/sites-affecter.php?limit=1000&statut_site=1&supprimer=0&affecter_site=0`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (response.data?.success) {
            sitesData = response.data.data || [];
          }
        } catch (err) {
          console.error("Erreur sites :", err);
        }
        setSites(sitesData);
      } catch (err) {
        console.error("Erreur générale:", err);
        setError("Impossible de charger les données nécessaires.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userInfo, navigate]);

  const controleurOptions = controleurs.map((ctrl) => ({
    value: ctrl.id_user || ctrl.id,
    label:
      ctrl.prenom && ctrl.nom
        ? `${ctrl.prenom} ${ctrl.nom}`
        : ctrl.nom_user || `Contrôleur ${ctrl.id_user || ctrl.id}`,
  }));

  const siteOptions = sites.map((site) => ({
    value: site.id_site,
    label: `${site.nom_site} - ${site.addresse_site || "Adresse non renseignée"}`,
  }));

  const handleSelectAll = () => {
    if (selectedSites.length === sites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(sites.map((s) => s.id_site));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setResults(null);
    if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);

    if (!selectedControleur) {
      setError("Veuillez sélectionner un contrôleur.");
      return;
    }
    if (selectedSites.length === 0) {
      setError("Veuillez sélectionner au moins un site.");
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Session expirée");
      setTimeout(() => navigate("/login"), 2000);
      setSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/affectations/controleur-create.php`,
        {
          id_user: parseInt(selectedControleur),
          id_site: selectedSites.map((id) => parseInt(id)),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      if (data.success && data.results) {
        const succeeded = data.results.filter((r) => r.success).length;
        const failed = data.results.length - succeeded;

        setResults({
          succeeded,
          failed,
          total: data.results.length,
          details: data.results,
        });

        if (failed === 0) {
          setSuccess(`${succeeded} affectation(s) créée(s) avec succès.`);
          setSelectedControleur("");
          setSelectedSites([]);
          redirectTimeoutRef.current = setTimeout(() => {
            navigate("/affectations-controleurs");
          }, 1000);
        } else {
          const failedSites = data.results
            .filter((r) => !r.success)
            .map((r) => r.nom_site || `Site #${r.id_site}`)
            .join(", ");
          setError(
            `${failed} affectation(s) ont échoué (${failedSites}). Vérifiez les doublons ou les permissions.`,
          );
        }
      } else {
        setError("Réponse inattendue du serveur.");
      }
    } catch (err) {
      console.error("Erreur lors de l'envoi:", err);
      if (err.response) {
        setError(err.response.data?.message || `Erreur ${err.response.status}`);
      } else {
        setError("Une erreur technique est survenue.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Affecter un contrôleur
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link
                  to="/affectations-controleurs"
                  style={{ color: COLORS.primary }}
                >
                  Affectations
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Chargement...
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "none",
            }}
          >
            <div className="card-body text-center py-5">
              <div
                className="spinner-border"
                role="status"
                style={{ color: COLORS.primary }}
              />
              <p className="mt-2" style={{ color: COLORS.darkText }}>
                Chargement des données...
              </p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
          Affecter un contrôleur à des sites
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>
                Tableau de bord
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link
                to="/affectations-controleurs"
                style={{ color: COLORS.primary }}
              >
                Affectations Contrôleurs
              </Link>
            </li>
            <li
              className="breadcrumb-item active"
              style={{ color: COLORS.secondary }}
            >
              Nouvelle affectation
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "none",
          }}
        >
          <div className="card-body">
            {/* Titre avec icône */}
            <h5
              className="card-title mb-4"
              style={{
                color: COLORS.primary,
                borderBottom: `2px solid ${COLORS.lightBg}`,
                paddingBottom: "16px",
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#E3F2FD",
                    color: COLORS.primary,
                  }}
                >
                  <i className="bi bi-person-plus"></i>
                </div>
                Nouvelle affectation
              </div>
            </h5>

            {/* Messages de feedback */}
            {error && (
              <div
                className="alert alert-danger d-flex align-items-center alert-dismissible fade show"
                style={{
                  backgroundColor: "#ffebee",
                  borderColor: COLORS.danger,
                  color: "#c62828",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.danger}`,
                }}
              >
                <i className="bi bi-exclamation-triangle me-2"></i>
                <div className="flex-grow-1">{error}</div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError("")}
                />
              </div>
            )}

            {success && (
              <div
                className="alert alert-success d-flex align-items-center alert-dismissible fade show"
                style={{
                  backgroundColor: "#e8f5e9",
                  borderColor: COLORS.success,
                  color: "#2e7d32",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.success}`,
                }}
              >
                <i className="bi bi-check-circle me-2"></i>
                <div className="flex-grow-1">{success}</div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccess("")}
                />
              </div>
            )}

            {results && (
              <div
                className="alert alert-info"
                style={{
                  backgroundColor: "#E3F2FD",
                  borderColor: COLORS.secondary,
                  color: COLORS.primary,
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.secondary}`,
                }}
              >
                <strong>Résultat :</strong> {results.succeeded} sur{" "}
                {results.total} réussie(s).
                {results.failed > 0 && (
                  <div className="mt-2">
                    <ul className="mb-0">
                      {results.details
                        .filter((r) => !r.success)
                        .map((r, idx) => (
                          <li key={idx}>
                            {r.nom_site || `Site #${r.id_site}`} :{" "}
                            {r.message || "Échec"}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Carte : Sélection du contrôleur */}
              <div
                className="card mb-4"
                style={{
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: "10px",
                }}
              >
                <div
                  className="card-header"
                  style={{
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    padding: "12px 16px",
                  }}
                >
                  <h6
                    className="card-title mb-0"
                    style={{ color: COLORS.primary, fontWeight: "600" }}
                  >
                    <i
                      className="bi bi-shield-check me-2"
                      style={{ color: COLORS.secondary }}
                    ></i>
                    Contrôleur <span className="text-danger">*</span>
                  </h6>
                </div>
                <div className="card-body">
                  <div className="text-muted small mb-2">
                    <i className="bi bi-info-circle me-1"></i>
                    {controleurs.length} contrôleur(s) disponible(s)
                  </div>
                  <SelectWithSearch
                    options={controleurOptions}
                    value={selectedControleur}
                    onChange={setSelectedControleur}
                    placeholder="-- Sélectionnez un contrôleur --"
                  />
                  {controleurs.length === 0 && (
                    <div className="form-text text-warning mt-2">
                      Aucun contrôleur actif disponible dans votre société.
                    </div>
                  )}
                </div>
              </div>

              {/* Carte : Sélection des sites */}
              <div
                className="card mb-4"
                style={{
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: "10px",
                }}
              >
                <div
                  className="card-header d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    padding: "12px 16px",
                  }}
                >
                  <h6
                    className="card-title mb-0"
                    style={{ color: COLORS.primary, fontWeight: "600" }}
                  >
                    <i
                      className="bi bi-building me-2"
                      style={{ color: COLORS.secondary }}
                    ></i>
                    Sites à affecter <span className="text-danger">*</span>
                  </h6>
                  {sites.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm fw-semibold"
                      onClick={handleSelectAll}
                      style={{
                        color: COLORS.secondary,
                        borderColor: COLORS.secondary,
                        backgroundColor: "transparent",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "0.85rem",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#E3F2FD";
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                    >
                      {selectedSites.length === sites.length
                        ? "Tout désélectionner"
                        : "Tout sélectionner"}
                    </button>
                  )}
                </div>
                <div className="card-body">
                  <div className="text-muted small mb-2">
                    <i className="bi bi-info-circle me-1"></i>
                    {sites.length} site(s) disponible(s) – Seuls les sites non
                    affectés sont affichés.
                  </div>
                  {sites.length === 0 ? (
                    <div className="alert alert-warning mb-0">
                      Aucun site actif disponible dans votre société.
                    </div>
                  ) : (
                    <MultiSelectWithSearch
                      options={siteOptions}
                      value={selectedSites}
                      onChange={setSelectedSites}
                      placeholder="-- Sélectionnez des sites --"
                    />
                  )}
                  {selectedSites.length > 0 && (
                    <div className="mt-2 text-end">
                      <span
                        className="badge"
                        style={{
                          backgroundColor: COLORS.infoLight,
                          color: COLORS.info,
                          padding: "4px 8px",
                        }}
                      >
                        <i className="bi bi-check-circle me-1"></i>
                        {selectedSites.length} site(s) sélectionné(s)
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="d-flex gap-3 justify-content-end mt-4">
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={() => navigate("/affectations-controleurs")}
                  disabled={submitting}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.backgroundColor = "#E3F2FD";
                      e.target.style.borderColor = COLORS.secondary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.borderColor = COLORS.primary;
                    }
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={
                    submitting || controleurs.length === 0 || sites.length === 0
                  }
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: "8px",
                    padding: "10px 32px",
                    transition: "all 0.2s ease",
                    opacity:
                      submitting ||
                      controleurs.length === 0 ||
                      sites.length === 0
                        ? 0.6
                        : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !submitting &&
                      controleurs.length > 0 &&
                      sites.length > 0
                    ) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Créer les affectations
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles repris de la page détail */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .modal-title,
        .alert strong,
        .table th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .modal-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table td,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .btn:focus,
        .form-control:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-check-input:checked {
          background-color: ${COLORS.primary};
          border-color: ${COLORS.primary};
        }
        
        .form-check-input:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .d-flex.gap-3 {
            flex-direction: column;
          }
          
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AffectationsControleursCreate;
